import {useCallback, useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {
  TOKENIZATION_CONFIG_QUERY_KEY,
  useTokenizationConfig,
} from '../../../../queries/paymentCard/useTokenizationConfig';
import {logger} from '../../../../utils/logger';

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

interface UseTillIntegrationKeyResult {
  /** The integration key, null if not yet fetched or on error */
  integrationKey: string | null;
  /** Whether a fetch is in progress */
  isLoading: boolean;
  /** Error from the last fetch attempt, null if successful */
  error: Error | null;
  /** Manually trigger a retry (resets retry count) */
  retry: () => void;
  /** Reset all state (call when modal closes) */
  reset: () => void;
}

/**
 * Enhanced hook to fetch Till integration key with exponential backoff retry.
 *
 * @param isVisible - Whether the modal is visible (triggers fetch when true)
 * @returns Integration key state and control functions
 */
export const useTillIntegrationKey = (isVisible: boolean): UseTillIntegrationKeyResult => {
  const queryClient = useQueryClient();
  const {
    data,
    isLoading: isQueryLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useTokenizationConfig(isVisible);

  const [retryError, setRetryError] = useState<Error | null>(null);

  // Track retry count in ref to avoid stale closure issues
  const retryCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Reset retry count on successful data fetch
  useEffect(() => {
    if (data?.public_integration_key) {
      retryCountRef.current = 0;
      setRetryError(null);
    }
  }, [data]);

  // Handle query error with retry logic
  useEffect(() => {
    if (queryError && isVisible) {
      const errorInstance =
        queryError instanceof Error ? queryError : new Error(String(queryError));

      logger.warn(
        'useTillIntegrationKey',
        `Fetch failed (attempt ${retryCountRef.current + 1}/${MAX_RETRIES})`,
        {
          error: errorInstance.message,
        },
      );

      if (retryCountRef.current < MAX_RETRIES - 1) {
        // Exponential backoff: 1s, 2s, 4s...
        const delay = BASE_RETRY_DELAY_MS * Math.pow(2, retryCountRef.current);
        retryCountRef.current += 1;
        timeoutRef.current = setTimeout(() => {
          refetch();
        }, delay);
      } else {
        logger.error('useTillIntegrationKey', 'All retry attempts exhausted', {
          error: errorInstance.message,
        });
        setRetryError(errorInstance);
      }
    }
  }, [queryError, isVisible, refetch]);

  const retry = useCallback(() => {
    clearTimeoutRef();
    retryCountRef.current = 0;
    setRetryError(null);
    refetch();
  }, [clearTimeoutRef, refetch]);

  const reset = useCallback(() => {
    clearTimeoutRef();
    setRetryError(null);
    retryCountRef.current = 0;
    // Remove cached query data so next open fetches fresh
    queryClient.removeQueries({queryKey: TOKENIZATION_CONFIG_QUERY_KEY});
  }, [clearTimeoutRef, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeoutRef();
    };
  }, [clearTimeoutRef]);

  // Derive state directly from query - no local state duplication
  const integrationKey = data?.public_integration_key ?? null;
  const isLoading = isVisible && (isQueryLoading || isFetching);
  const error = retryError ?? (queryError instanceof Error ? queryError : null);

  return {
    integrationKey,
    isLoading,
    error,
    retry,
    reset,
  };
};

export default useTillIntegrationKey;
